import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import { BD_AGE_GROUPS_DISPLAY_NAMES } from '@bindoc-projects/virtual-clinic-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const BD_AGM_CONDITION_ID = 'agmId';
export const FilterConditionAgmPluginType: FilterConditionPluginSpecification<
  number[],
  FilterConditionAgmDto,
  BdAgmCondition
> = {
  type: 'ageGroups',
  category: 'person',
  displayName: { de: 'Altersgruppen' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionAgmDto,
  ): FilterConditionPluginImpl<number[]> {
    return new FilterConditionAgmPlugin(obj.agm);
  },
  fromCondition(
    jsonObj: BdAgmCondition,
  ): FilterConditionPluginImpl<number[]> {
    return new FilterConditionAgmPlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<number[]> {
    return new FilterConditionAgmPlugin([]);
  },
};

export class FilterConditionAgmPlugin extends FilterConditionPluginImpl<
  number[],
  FilterConditionAgmDto
> {
  static override Specification: FilterConditionPluginSpecification<
    number[],
    FilterConditionAgmDto,
    BdAgmCondition
  > = FilterConditionAgmPluginType;

  constructor(private agm: number[]) {
    super(FilterConditionAgmPluginType.type);
  }

  clone(): FilterConditionAgmPlugin {
    return new FilterConditionAgmPlugin([...this.agm]);
  }

  override equals(other: FilterConditionAgmPlugin): boolean {
    return super.equals(other) && BdArrayUtil.equals(this.agm, other.agm);
  }

  override validationToText(): string {
    if (this.agm.length === 0) {
      return 'Mindestens eine Altersgruppe auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdAgmCondition(this.agm, []);
  }

  toJSON(): FilterConditionAgmDto | null {
    return {
      type: this.type,
      agm: this.agm,
    };
  }

  toText(): string | null {
    return this.agm.length === 1
      ? 'eine Altersgruppe'
      : `${this.agm.length} Altersgruppen`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const sorted = [...this.agm].sort((a, b) => a - b);
    const items = sorted.map((id) => {
      const displayName = (
        BD_AGE_GROUPS_DISPLAY_NAMES as Record<number, string>
      )[id];
      return displayName ?? String(id);
    });
    return { title, items };
  }

  toValue(): number[] {
    return this.agm;
  }
}

export class BdAgmCondition extends BdContentCondition<number> {
  constructor(includes: number[], excludes: number[]) {
    super(BD_AGM_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionAgmDto {
  type: string;
  agm: number[];
}
